import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { InteractionRequiredAuthError, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { msalConfig, tenantId } from '../../environments/environment';
import { AccessForbiddenComponent } from '../components/access-forbidden/access-forbidden/access-forbidden.component';
import { AccessForbidden } from '../models/access-forbidden';
import { Delegati, InfoUser } from '../models/content.model';
import { LoginResponse } from '../models/login-response.model';
import { ConfigurationService } from './configuration.service';
import { DBkeys } from './db-keys';
import { JwtHelper } from './jwt-helper';
import { Utilities } from './utilities';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationEndpointService {

  constructor(
    protected http: HttpClient, 
    protected configurations: ConfigurationService, 
    private msalAuthService: MsalService,
    private router: Router,
    public dialog: MatDialog) { }

  private getAzureToken(): string {
    var accountInfo = this.msalAuthService.instance.getAllAccounts()[0];
    var keys = Object.keys(localStorage);
    var key: any = keys.find(x => x.includes(accountInfo.homeAccountId + "-" + 
    accountInfo.environment + "-idtoken-" + 
      msalConfig.auth.clientId + "-" + 
      tenantId));

    var secret = JSON.parse(localStorage.getItem(key)).secret;

    return secret;
  }

  protected getRequestHeaders(): { headers: HttpHeaders | { [header: string]: string | string[]; } } {
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
      "Accept": "application/json, text/plain, */*"
    });

    return { headers: headers };
  }

  protected getAuthorizationRequestHeaders(): { headers: HttpHeaders | { [header: string]: string | string[]; } } {

    var bearerToken = "";
    if (this.accessToken)
      bearerToken = this.accessToken;
    else
      console.error("Impossibile recuperare l'access token.");

    const headers = new HttpHeaders({
      "Authorization": "Bearer " + bearerToken,
      "Content-Type": "application/json",
      "Accept": "application/json, text/plain, */*"
    });

    return { headers: headers };
  }

  public getSecureApiToken$(): Observable<LoginResponse> {
    const header = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    var request = new AcquireSecureApiTokenRequest();
    request.Username = "{AZURETOKEN.rada}";
    request.Password = this.getAzureToken();

    return this.http.post<LoginResponse>(this.configurations.baseUrl + "/api/auth/login", JSON.stringify(request), { headers: header }).pipe<LoginResponse>(
      catchError(error => {
        return this.handleError(error, () => this.getSecureApiToken$());
      })
    );
  }

  getRefreshLoginEndpoint$(): Observable<LoginResponse> {
    const header = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    const requestBody = new RefreshRequest();
    var refreshToken = localStorage.getItem("refresh_token");
    if(refreshToken)
      requestBody.RefreshToken = refreshToken;
    else {
      console.error("Impossibile recuperare il refresh token.");
      return;
    }

    return this.http.post<LoginResponse>(this.configurations.baseUrl + "/api/auth/refresh", JSON.stringify(requestBody), { headers: header }).pipe<LoginResponse>(
      catchError(error => {
        return this.handleError(error, () => this.getRefreshLoginEndpoint$());
      })
    );
  }

  getDelegatiEndpoint$(): Observable<Array<Delegati>> {
    return this.http.get<Array<Delegati>>(this.configurations.baseUrl + "/api/playlist/delegati", this.getAuthorizationRequestHeaders()).pipe<Array<Delegati>>(
      catchError(error => {
        return this.handleError(error, () => this.getDelegatiEndpoint$());
      })
    );
  }

  getInfoUserEndpoint$(): Observable<InfoUser> {
    return this.http.get<InfoUser>(this.configurations.baseUrl + "/api/auth/info_user", this.getAuthorizationRequestHeaders()).pipe<InfoUser>(
      catchError(error => {
        return this.handleError(error, () => this.getInfoUserEndpoint$());
      })
    );
  }

  getDelegationEndpoint$(deleganteId: string): Observable<LoginResponse> {
    const requestBody = new DelegationRequest();
    requestBody.DeleganteId = deleganteId;

    return this.http.post<LoginResponse>(this.configurations.baseUrl + "/api/auth/delegation", JSON.stringify(requestBody), this.getAuthorizationRequestHeaders()).pipe<LoginResponse>(
      catchError(error => {
        return this.handleError(error, () => this.getDelegationEndpoint$(deleganteId));
      })
    );
  }

  getLogoutDelegaEndpoint() {
    const header = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.post<any>(this.configurations.baseUrl + "/api/auth/logoutDelegation", "{}", { headers: header }).pipe(
      catchError(error => {
        return this.handleError(error, () => this.getLogoutDelegaEndpoint() );
      })
    );
  }

  silentRefresh(callback?: Function) {
    var account = this.msalAuthService.instance.getAllAccounts()[0];
    var username = account.username;
    var silentRequest = {
      scopes: ['user.read'],
      account: account,
      forceRefresh: false
    };

    var request: RedirectRequest = {
      scopes: ['user.read'],
      loginHint: username
    };

    this.msalAuthService.acquireTokenSilent(silentRequest).subscribe(resp => {
      const jwtHelper = new JwtHelper();
      if(jwtHelper.isTokenExpired(resp.idToken)) {
        this.msalAuthService.loginRedirect();
        return;
      }
      this.getRefreshLoginEndpoint$().subscribe(r => {
        Utilities.processLoginResponse(r);
        if(callback) callback();
      });
    },
    error => {
      if(error instanceof InteractionRequiredAuthError) {
        return this.msalAuthService.acquireTokenRedirect(request).pipe(
          catchError(error => {
            return this.handleError(error);
          })
        );
      }
    });
  }

  protected handleError(error: any, callback?: Function) {
    const correlationId = Utilities.getCorrelationId(error);

    if (error.status == 401) {
      this.silentRefresh(callback);
      return;
    } else if(error?.error?.error === "invalid_grant") {
      var errorData = new AccessForbidden();
      errorData.errorCode = error.status;
      errorData.errorDescription = error?.error?.error_description ?? "Non hai il ruolo necessario per poter accedere all'applicazione.";
      // this.router.navigate(['/access-forbidden'], {state: {data: errorData}});
      const dialogRef = this.dialog.open(AccessForbiddenComponent, {
        width: '500px',
        data: errorData,
      });

      dialogRef.afterClosed().subscribe(result => {
        this.msalAuthService.logout();
      });
    }

    Utilities.showError(error, correlationId);
    return throwError(error);
  }

  private get accessToken(): string {
    return localStorage.getItem(DBkeys.ACCESS_TOKEN);
  } 
}

class AcquireSecureApiTokenRequest {
  public Username: string;       
  public Password: string;
}

class DelegationRequest {
  public DeleganteId: string;
}

class RefreshRequest {
  public RefreshToken: string;
}
